import axios from "axios";
import axiosInstance from "@/lib/axios";
import type {
  LoginRequest,
  LoginSuccessResponse,
  LoginErrorResponse,
  RequestOtpPayload,
  RequestOtpSuccessResponse,
  RequestOtpErrorResponse,
  RegisterRequest,
  RegisterSuccessResponse,
  RegisterErrorResponse,
  RefreshTokenPayload,
  RefreshTokenResponse,
  ApiErrorResponse,
  RequestOtpLoginPayload,
  RequestOtpLoginSuccessResponse,
  RequestOtpLoginErrorResponse,
  VerifyOtpPayload,
  VerifyOtpSuccess,
  VerifyOtpError,
  ResendOtpPayload,
  ResendOtpSuccessResponse,
  ResendOtpErrorResponse,
  LogoutSuccessResponse,
  RequestOtpPasswordResetPayload,
  RequestOtpPasswordResetSuccessResponse,
  RequestOtpPasswordResetErrorResponse,
  ResetPasswordWithOtpPayload,
  ResetPasswordWithOtpSuccessResponse,
  ResetPasswordWithOtpErrorResponse,
} from "@/types/auth";

import { useUserStore } from "@/stores/useAuthStore";
import { setCookie, deleteCookie } from "@/lib/cookies";

const LOGIN_URL = "/auth/login";
const REQUEST_OTP_URL = "/auth/request-otp";
const REGISTER_URL = "/auth/verify-otp-and-register";
const REFRESH_TOKEN_URL = "/auth/refresh-token";
const REQUEST_OTP_LOGIN_URL = "/auth/request-otp-for-login";
const LOGIN_OTP_URL = "/auth/verify-otp-and-login";
const RESEND_OTP_URL = "/auth/resend-otp";
const LOGOUT_URL = "/logout";
const REQUEST_OTP_PASSWORD_RESET_URL = "/auth/request-otp-for-password-reset";
const RESET_PASSWORD_WITH_OTP_URL = "/auth/reset-password-with-otp";

import {
  ACCESS_TOKEN_EXPIRE_DAYS as accessTokenExpireDays,
  REFRESH_TOKEN_EXPIRE_DAYS as refreshTokenExpireDays,
} from "@/lib/auth.config";

export async function login(payload: LoginRequest) {
  try {
    const { data } = await axiosInstance.post<LoginSuccessResponse>(
      LOGIN_URL,
      payload,
    );

    if (!data.succeed) throw data;

    if (data.data?.tokens?.access_token) {
      setCookie("access_token", data.data.tokens.access_token, {
        days: accessTokenExpireDays,
      });
    }

    if (data.data?.tokens?.refresh_token) {
      setCookie("refresh_token", data.data.tokens.refresh_token, {
        days: refreshTokenExpireDays,
      });
    }

    const userProfile = data.data?.user_profile;
    if (userProfile) {
      useUserStore.getState().setUser(userProfile);
    }
    return data;
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      const apiError = err.response?.data as LoginErrorResponse;
      throw apiError;
    }
    throw err;
  }
}

export async function registerUser(payload: RegisterRequest) {
  try {
    const { data } = await axiosInstance.post<RegisterSuccessResponse>(
      REGISTER_URL,
      payload,
    );

    if (!data.succeed) throw data;

    if (data.data?.tokens?.access_token) {
      setCookie("access_token", data.data.tokens.access_token, {
        days: accessTokenExpireDays,
      });
    }

    if (data.data?.tokens?.refresh_token) {
      setCookie("refresh_token", data.data.tokens.refresh_token, {
        days: refreshTokenExpireDays,
      });
    }

    const userProfile = data.data?.user_profile;
    if (userProfile) {
      useUserStore.getState().setUser(userProfile);
    }

    // The backend only includes qr_discount_code when this signup used a
    // tracked qr_code and earned a discount — its presence alone is the
    // signal to show the modal, no separate flag needed.
    const discount = data.data?.qr_discount_code;

    return { ...data, discount };
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      const apiError = err.response?.data as RegisterErrorResponse;
      throw apiError;
    }
    throw err;
  }
}

export async function verifyOtpAndLogin(
  payload: VerifyOtpPayload,
): Promise<VerifyOtpSuccess> {
  try {
    const { data } = await axiosInstance.post<VerifyOtpSuccess>(
      LOGIN_OTP_URL,
      payload,
    );

    if (data.data?.tokens?.access_token) {
      setCookie("access_token", data.data.tokens.access_token, {
        days: accessTokenExpireDays,
      });
    }

    if (data.data?.tokens?.refresh_token) {
      setCookie("refresh_token", data.data.tokens.refresh_token, {
        days: refreshTokenExpireDays,
      });
    }

    const userProfile = data.data?.user_profile;
    if (userProfile) {
      useUserStore.getState().setUser(userProfile);
    }

    return data;
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      const errorData = err.response?.data as VerifyOtpError;
      throw {
        ...errorData,
        status: err.response?.status,
        message:
          errorData?.error?.messages?.[0] ||
          errorData?.reason ||
          "خطا در تایید کد",
      };
    }
    throw err;
  }
}

export async function refreshToken(
  payload: RefreshTokenPayload,
): Promise<RefreshTokenResponse> {
  try {
    const { data } = await axiosInstance.post<RefreshTokenResponse>(
      REFRESH_TOKEN_URL,
      payload,
    );
    return data;
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      const apiError = err.response?.data as ApiErrorResponse;
      throw apiError;
    }
    throw err;
  }
}

export async function requestOtp(payload: RequestOtpPayload) {
  try {
    const { data } = await axiosInstance.post<RequestOtpSuccessResponse>(
      REQUEST_OTP_URL,
      payload,
    );

    return data;
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      const apiError = err.response?.data as RequestOtpErrorResponse;
      throw apiError;
    }
    throw err;
  }
}

export async function requestOtpForLogin(payload: RequestOtpLoginPayload) {
  try {
    const { data } = await axiosInstance.post<RequestOtpLoginSuccessResponse>(
      REQUEST_OTP_LOGIN_URL,
      payload,
    );

    return data;
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      const apiError = err.response?.data as RequestOtpLoginErrorResponse;
      throw apiError;
    }
    throw err;
  }
}

export async function resendOtp(
  payload: ResendOtpPayload,
): Promise<ResendOtpSuccessResponse> {
  try {
    const { data } = await axiosInstance.post<ResendOtpSuccessResponse>(
      RESEND_OTP_URL,
      payload,
    );

    return data;
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      const apiError = err.response?.data as ResendOtpErrorResponse;
      throw apiError;
    }
    throw err;
  }
}

export async function logout(): Promise<LogoutSuccessResponse> {
  await Promise.allSettled([
    fetch("/auth/clear", { method: "POST" }),
    axiosInstance.post<LogoutSuccessResponse>(LOGOUT_URL, {}),
  ]);

  deleteCookie("access_token");
  deleteCookie("refresh_token");

  useUserStore.getState().clearUser();

  return { succeed: true } as LogoutSuccessResponse;
}

export async function requestOtpForPasswordReset(
  payload: RequestOtpPasswordResetPayload,
) {
  try {
    const { data } =
      await axiosInstance.post<RequestOtpPasswordResetSuccessResponse>(
        REQUEST_OTP_PASSWORD_RESET_URL,
        payload,
      );

    return data;
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      const apiError = err.response
        ?.data as RequestOtpPasswordResetErrorResponse;
      throw apiError;
    }
    throw err;
  }
}

export async function resetPasswordWithOtp(
  payload: ResetPasswordWithOtpPayload,
): Promise<ResetPasswordWithOtpSuccessResponse> {
  try {
    const { data } =
      await axiosInstance.post<ResetPasswordWithOtpSuccessResponse>(
        RESET_PASSWORD_WITH_OTP_URL,
        payload,
      );

    return data;
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      const apiError = err.response?.data as ResetPasswordWithOtpErrorResponse;
      throw apiError;
    }
    throw err;
  }
}
